import { Helmet } from "react-helmet-async";
import { type MouseEvent, type ReactNode, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { PaymentProcessorsBadge } from "@/components/icons/PaymentLogos";
import { stages } from "@/components/SN_IdeaToLaunchSection";
import { W } from "@/platform/ui";
import { OfferHeader, OfferSec, OfferShell, PricePanel, TermsLine, offerBtn } from "@/platform/offer";

// The services catalogue (26 Oct 2026, his revision of phase 1): a band under the site bar, a facts strip and
// one dense screener-style table in his order: For firms, Programs, Sessions and consultations, Launchpad.
// The free Quick Consultation is hidden (page kept, unlisted) and Vibe Engineering retired, 1 Nov 2026. Each row opens its own page; nothing here takes a payment.
// The AI Research Consultation row and the masterclasses were taken out on his word (the consultation page
// stays live, unlisted). Mentorship, Agentic AI Consultation and the Command Centre were retired on 1 Nov 2026.

type Group = { id: string; label: string };

const GROUPS: Group[] = [
  { id: "firms", label: "For firms" },
  { id: "mentorship", label: "Programs" },
  { id: "sessions", label: "Sessions and consultations" },
  { id: "launchpad", label: "Launchpad" },
];

interface Service {
  id: string;
  group: string;
  badge: string;
  title: string;
  description: ReactNode;
  format: string;
  length: string;
  price: string;
  priceNote?: string;
  cta: string;
  href: string;
}

const linkCls = "font-medium text-foreground underline decoration-[var(--sn-border-strong)] underline-offset-4 hover:decoration-foreground";

const Services = () => {
  const location = useLocation();
  const { isIndian, servicePrices, currency } = useGeoCurrency();
  const launchpadPrice = isIndian ? "₹1,00,000" : "$1,200";

  const services = useMemo<Service[]>(() => [
    {
      id: "enterprise",
      group: "firms",
      badge: "Enterprise",
      title: "AI Implementation, Training & Automation for Modern Firms",
      description: "We help finance, wealth, real estate and research firms deploy AI tools, automate workflows and train their teams.",
      format: "Team engagement",
      length: "By scope",
      price: "",
      cta: "Explore details",
      href: "/enterprise",
    },
    {
      id: "agentic-research-systems",
      group: "mentorship",
      badge: "Waitlist",
      title: "Agentic Research Systems for Stock Investors",
      description: "A hands-on three-month program to build your own AI equity research system. Limited seats.",
      format: "Program",
      length: "3 months",
      price: servicePrices.agenticResearchSystems,
      priceNote: isIndian ? "Exclusive of GST" : undefined,
      cta: "Join the waitlist",
      href: "/agentic-research-systems",
    },
    {
      id: "ai-session",
      group: "sessions",
      badge: "1:1 session",
      title: "AI for Financial Research",
      description: "A focused 120-minute session on using AI for financial research, built around your workflow.",
      format: "1:1 session",
      length: "120 min",
      price: servicePrices.aiFinancialResearch,
      cta: "Book a session",
      href: "/ai-financial-research",
    },
    {
      id: "launchpad",
      group: "launchpad",
      badge: stages.join(" → "),
      title: "Launchpad",
      description: "We help you turn a startup idea into a live product plan, which we can then implement.",
      format: "Consultation",
      length: "By scope",
      price: launchpadPrice,
      cta: "Book a free discovery call",
      href: "/free-discovery-call",
    },
  ], [servicePrices, isIndian, launchpadPrice]);

  const offer = (name: string, url: string, price: string) => ({
    "@type": "Offer",
    name,
    url: `https://shikshannivesh.com${url}`,
    price,
    priceCurrency: currency,
  });
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Shikshan Nivesh Services",
    "description": "Programs, sessions and consultations on using AI for investment research, led by Shubham Borkar.",
    "url": "https://shikshannivesh.com/services",
    "numberOfItems": 3,
    "itemListElement": [
      offer("Agentic Research Systems for Stock Investors", "/agentic-research-systems", isIndian ? "100000" : "3000"),
      offer("AI for Financial Research", "/ai-financial-research", isIndian ? "10000" : "199"),
      offer("Launchpad", "/free-discovery-call", isIndian ? "100000" : "1200"),
    ].map((item, i) => ({ "@type": "ListItem", position: i + 1, item })),
  };

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      const el = [document.getElementById(id), document.getElementById(`${id}-m`)].find((x) => x && x.getClientRects().length > 0);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  // the table is drawn twice (wide and phone), so a group link scrolls to whichever copy is on screen
  const jump = (id: string) => (e: MouseEvent) => {
    const el = [document.getElementById(id), document.getElementById(`${id}-m`)].find((x) => x && x.getClientRects().length > 0);
    if (!el) return;
    e.preventDefault();
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  };
  const priceCell = (s: Service, cls = "") =>
    s.price ? (
      <span className={cls}>
        <span className="font-semibold text-foreground [font-variant-numeric:tabular-nums]">{s.price}</span>
        {s.priceNote && <span className="block text-[11.5px] font-normal text-muted-foreground">{s.priceNote}</span>}
      </span>
    ) : (
      <span className="font-mono text-[12px] text-muted-foreground">By proposal</span>
    );
  const actionCls = "inline-flex h-7 items-center justify-center whitespace-nowrap rounded-[3px] border border-[var(--sn-border-strong)] bg-[hsl(var(--card))] px-2.5 text-[12.5px] font-semibold text-foreground transition-colors hover:border-foreground";
  const lead = "Programs, sessions and consultations on using AI for investment research, led by Shubham Borkar.";

  return (
    <>
      <Helmet>
        <title>Services - Shikshan Nivesh</title>
        <meta name="description" content={lead} />
        <link rel="canonical" href="https://shikshannivesh.com/services" />
        <meta property="og:title" content="Services - Shikshan Nivesh" />
        <meta property="og:description" content={lead} />
        <meta property="og:url" content="https://shikshannivesh.com/services" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>
      <OfferShell>
        {/* The services band, drawn like SN Data's platform bar: where you are, the groups, one plain link. */}
        <div className="sticky top-[56px] z-40 border-b border-[var(--sn-border)] bg-[hsl(var(--card))]/95 backdrop-blur-xl lg:top-[60px]">
          <W className="flex h-[46px] items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-5">
              <Link to="/services" className="hidden shrink-0 font-mono text-[11.5px] font-semibold uppercase tracking-[0.14em] text-foreground sm:block">
                SN Services
              </Link>
              <nav aria-label="Service groups" className="flex min-w-0 gap-0.5 overflow-x-auto [scrollbar-width:none]">
                {GROUPS.map((g) => (
                  <a key={g.id} href={`#${g.id}`} onClick={jump(g.id)} className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground">
                    {g.label}
                  </a>
                ))}
              </nav>
            </div>
          </W>
        </div>

        <OfferHeader
          crumbs={[["/", "Home"], [null, "Services"]]}
          label="Services · Catalogue"
          title="Services"
          lead={<p>{lead}</p>}
        >
          <p className="mt-3 font-mono text-[12px] text-muted-foreground">International payments accepted in USD · info@shikshannivesh.com</p>
        </OfferHeader>

        <W>

          <section id="catalogue" className="scroll-mt-[120px] pt-6">
            {/* Wide screens: one screener-style table. Phones: the same rows stacked. */}
            <div className="hidden overflow-x-auto rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))] md:block">
              <table className="w-full min-w-[860px] border-collapse text-[13.5px]">
                <colgroup>
                  <col />
                  <col className="w-[130px]" />
                  <col className="w-[100px]" />
                  <col className="w-[180px]" />
                  <col className="w-[190px]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-[var(--sn-border-strong)] bg-[var(--snd-band)] text-left text-[12px] text-[var(--sn-fg2)]">
                    <th className="py-2 pl-4 pr-3 font-medium">Service</th>
                    <th className="px-3 py-2 font-medium">Format</th>
                    <th className="px-3 py-2 font-medium">Length</th>
                    <th className="px-3 py-2 text-right font-medium">Price</th>
                    <th className="py-2 pl-3 pr-4 text-right font-medium">Action</th>
                  </tr>
                </thead>
                {GROUPS.map((g) => {
                  const rows = services.filter((s) => s.group === g.id);
                  return (
                    <tbody key={g.id} id={g.id} className="scroll-mt-[130px]">
                      <tr className="border-b border-[var(--sn-border)] bg-[var(--snd-wash)]">
                        <td colSpan={4} className="px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{g.label}</td>
                        <td className="py-1.5 pl-3 pr-4 text-right font-mono text-[11px] text-muted-foreground">{rows.length}</td>
                      </tr>
                      {rows.map((s) => (
                        <tr key={s.id} className="border-b border-[var(--sn-border)] align-top last:border-b-0 hover:bg-[var(--snd-wash)]">
                          <td className="py-2.5 pl-4 pr-3">
                            <div className="flex flex-wrap items-baseline gap-x-2.5">
                              <Link to={s.href} className="text-[14px] font-semibold text-foreground hover:underline">{s.title}</Link>
                              <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">{s.badge}</span>
                            </div>
                            <p className="mt-0.5 max-w-[72ch] text-[12.5px] leading-[1.5] text-muted-foreground">{s.description}</p>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-[var(--sn-fg2)]">{s.format}</td>
                          <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] text-[var(--sn-fg2)]">{s.length}</td>
                          <td className="px-3 py-2.5 text-right">{priceCell(s)}</td>
                          <td className="py-2.5 pl-3 pr-4 text-right"><Link to={s.href} className={actionCls}>{s.cta}</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  );
                })}
              </table>
            </div>
            <div className="overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))] md:hidden">
              {GROUPS.map((g) => (
                <div key={g.id} id={`${g.id}-m`}>
                  <p className="border-b border-[var(--sn-border)] bg-[var(--snd-wash)] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{g.label}</p>
                  {services.filter((s) => s.group === g.id).map((s) => (
                    <div key={s.id} className="border-b border-[var(--sn-border)] px-4 py-3">
                      <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">{s.badge} · {s.format} · {s.length}</p>
                      <Link to={s.href} className="mt-0.5 block text-[15px] font-semibold text-foreground">{s.title}</Link>
                      <p className="mt-1 text-[13px] leading-[1.5] text-muted-foreground">{s.description}</p>
                      <div className="mt-2.5 flex items-center justify-between gap-3">
                        <span className="text-[14px]">{priceCell(s)}</span>
                        <Link to={s.href} className={actionCls}>{s.cta}</Link>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <TermsLine
              note={
                <>
                  International payments accepted in USD. Questions first? Email{" "}
                  <a href="mailto:info@shikshannivesh.com" className={linkCls}>info@shikshannivesh.com</a>.
                </>
              }
              links={[["/legal?section=refund", "Refunds"], ["/legal?section=terms", "Terms"]]}
              right={<PaymentProcessorsBadge />}
            />
          </section>

          <OfferSec id="founder" title="About the founder">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-10">
              <div className="min-w-0 max-w-[72ch] space-y-3 text-[14.5px] leading-[1.65] text-[var(--sn-fg2)]">
                <p>
                  I have been researching stocks since 2017, when I was still a mechanical engineering student, and I always
                  wanted to be a &ldquo;SuperAnalyst&rdquo;, the way a kid wants to be Superman, only for stock research. That
                  took me to an MBA and then to ICICI Securities, first as a research analyst and later as an investment
                  advisor to high-net-worth clients, with about $60 million of their money in equities.
                </p>
                <p>
                  Along the way I got obsessed with systems and frameworks, so I built one for my own research in Notion and
                  kept rebuilding it until it had to become software, which I learned to build myself with AI beside me. The
                  program and the sessions on this page are where I help other investors build AI into their own research
                  process, starting from the way they already work.
                </p>
                <Link to="/shubham-borkar" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-foreground hover:underline">
                  More about Shubham Borkar <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <dl className="self-start divide-y divide-[var(--sn-border)] overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))] text-[13px]">
                {([
                  ["Researching stocks", "Since 2017"],
                  ["Studied", "Mechanical engineering, then an MBA"],
                  ["ICICI Securities", "Research analyst, then investment advisor"],
                  ["Client equities advised", "About $60 million"],
                  ["Now", "Shikshan Nivesh and greeksoup.ai"],
                ] as [string, string][]).map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="text-right text-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </OfferSec>

          {/* The block above the footer: the research program. The discovery call stays on the Launchpad card only (his ruling, 1 Nov 2026). */}
          <OfferSec id="programs" title="The research program">
            <div className="grid gap-4 md:grid-cols-2">
              <PricePanel
                lead
                title="Agentic Research Systems for Stock Investors"
                right="3 months · waitlist"
                cells={[{ label: "Program", price: servicePrices.agenticResearchSystems, note: isIndian ? "Exclusive of GST" : undefined }]}
                action={<Link to="/agentic-research-systems" className={offerBtn.solid}>Join the waitlist <ArrowRight className="h-4 w-4" /></Link>}
              />
            </div>
          </OfferSec>
        </W>
      </OfferShell>
    </>
  );
};

export default Services;
